import type { AllOrientationsHoleSets, DetectionModel, DrillSide, HoleSet } from '@/types/detection.types';
import { apiClient } from './client';

export const detectionsApi = {
  // GET /detections/project/{project_id} - all detections for a project
  getByProjectId: async (projectId: string): Promise<DetectionModel[]> => {
    const { data } = await apiClient.get<DetectionModel[]>(`/detections/project/${projectId}`);
    return data;
  },

  // GET /detection/{extraction_id} - download original STP file
  downloadSTP: async (extractionId: string): Promise<Blob> => {
    const { data } = await apiClient.get<Blob>(`/detection/${extractionId}`, {
      responseType: 'blob',
    });
    return data;
  },

  // GET /detection/{extraction_id}/holes-count - get holes count for this extraction
  getHolesCount: async (extractionId: string): Promise<number> => {
    const { data } = await apiClient.get<{ holes_count: number }>(`/detection/${extractionId}/holes-count`);
    return data.holes_count;
  },

  // GET /detection/{extraction_id}/source - get HoleSet JSON for this extraction
  getSource: async (extractionId: string): Promise<HoleSet> => {
    const { data } = await apiClient.get<HoleSet>(`/detection/${extractionId}/source`);
    return data;
  },

  // GET /detection/{extraction_id}/holes - get all HoleSets for all orientations
  getAllHoles: async (extractionId: string): Promise<AllOrientationsHoleSets> => {
    const { data } = await apiClient.get<AllOrientationsHoleSets>(`/detection/${extractionId}/holes`);
    return data;
  },

  // GET /detection/{extraction_id}/hole/{orientation} - get HoleSet for specific orientation
  getHolesByOrientation: async (extractionId: string, orientation: DrillSide): Promise<HoleSet> => {
    const { data } = await apiClient.get<HoleSet>(`/detection/${extractionId}/hole/${orientation}`);
    return data;
  },

  // DELETE /detection/{extraction_id} - delete detection
  delete: async (extractionId: string): Promise<void> => {
    await apiClient.delete(`/detection/${extractionId}`);
  },

  // GET /detection/{extraction_id}/3d - get 3D preview PNG
  get3DPreview: async (extractionId: string): Promise<Blob> => {
    // Use apiClient to ensure consistent base URL and auth handling
    const { data } = await apiClient.get<Blob>(`/detection/${extractionId}/3d`, {
      responseType: 'blob',
    });
    return data;
  },

  // Get URL for 3D preview PNG (for direct image src usage)
  get3DPreviewUrl: (extractionId: string): string => {
    return `${apiClient.defaults.baseURL}/detection/${extractionId}/3d`;
  },

  // POST /detections/project/{project_id}/regenerate/background - re-run the extraction pipeline
  // for the project's one STEP document (there is no per-solid variant any more).
  regenerate: async (projectId: string): Promise<string> => {
    const { data } = await apiClient.post<{ uid: string }>(`/detections/project/${projectId}/regenerate/background`);
    return data.uid;
  },

  // GET /3d/task/{task_id}/status - get task status
  getTaskStatus: async (taskId: string): Promise<TaskStatus> => {
    const { data } = await apiClient.get<TaskStatus>(`/3d/task/${taskId}/status`);
    return data;
  },
};

export interface TaskStatus {
  task_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  current_step: string;
  created_at: string;
  updated_at: string;
  error: string | null;
  result: Record<string, unknown> | null;
}
